// Formatting utilities for display values

export function formatSpeed(
  speedMs: number,
  units: "metric" | "imperial" = "metric",
): string {
  if (units === "metric") {
    const kmh = speedMs * 3.6;
    return kmh.toFixed(1);
  } else {
    const mph = speedMs * 2.23694;
    return mph.toFixed(1);
  }
}

export function formatSpeedParts(
  speedMs: number,
  units: "metric" | "imperial" = "metric",
): { integer: string; decimal: string; value: number } {
  const speed = units === "metric" ? speedMs * 3.6 : speedMs * 2.23694;
  const formatted = speed.toFixed(1);
  const [integer, decimal] = formatted.split(".");
  return { integer, decimal, value: speed };
}

export function formatSpeedWithUnit(
  speedMs: number,
  units: "metric" | "imperial" = "metric",
): string {
  const formatted = formatSpeed(speedMs, units);
  const unit = units === "imperial" ? "mph" : "km/h";
  return `${formatted} ${unit}`;
}

export function formatDistance(
  meters: number,
  units: "metric" | "imperial" = "metric",
  forceMainUnit: boolean = false, // Force km or miles (not meters/feet)
): string {
  if (units === "metric") {
    // Always show in km if forceMainUnit is true (for when "km" label is shown)
    if (forceMainUnit || meters >= 1000) {
      const km = meters / 1000;
      // Show 2 decimal places until 1 km, then 1 decimal place
      return km.toFixed(km < 1 ? 2 : 1);
    } else {
      return `${Math.round(meters)}`;
    }
  } else {
    const miles = meters * 0.000621371;
    // Always show in miles if forceMainUnit is true (for when "mi" label is shown)
    if (forceMainUnit || miles >= 0.1) {
      // Show 2 decimal places until 1 mile, then 1 decimal place
      return miles.toFixed(miles < 1 ? 2 : 1);
    } else {
      const feet = meters * 3.28084;
      return `${Math.round(feet)}`;
    }
  }
}

export function formatDistanceWithUnit(
  meters: number,
  units: "metric" | "imperial" = "metric",
): string {
  const formatted = formatDistance(meters, units);
  const unit = units === "imperial" ? "mi" : "km";
  // Handle meter/feet fallback display
  if (units === "imperial" && meters * 0.000621371 < 0.1) {
    return `${formatted} ft`;
  } else if (units !== "imperial" && meters < 1000) {
    return `${formatted} m`;
  }
  return `${formatted} ${unit}`;
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }
}

export function formatDurationShort(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m ${seconds % 60}s`;
}

export function formatDateTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } else if (diffDays === 1) {
    return `Yesterday ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } else {
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}

export function formatCoordinate(
  lat: number,
  lng: number,
  precision: number = 5,
): string {
  const latStr = lat.toFixed(precision);
  const lngStr = lng.toFixed(precision);
  const latDir = lat >= 0 ? "N" : "S";
  const lngDir = lng >= 0 ? "E" : "W";
  return `${Math.abs(parseFloat(latStr))}°${latDir}, ${Math.abs(parseFloat(lngStr))}°${lngDir}`;
}

export function formatHeading(degrees: number): string {
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round((degrees % 360) / 45) % 8;
  return directions[index];
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function getUserInitial(username: string): string {
  if (!username) return "?";

  // Try to extract the first emoji using regex
  const emojiMatch = username.match(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F/gu);
  if (emojiMatch && emojiMatch.length > 0) {
    return emojiMatch[0];
  }

  // Otherwise return uppercase first letter
  return username.charAt(0).toUpperCase();
}
